// File: apps/web/src/components/ui/Button.tsx

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-950 disabled:pointer-events-none disabled:opacity-40 uppercase tracking-wide',
  {
    variants: {
      variant: {
        default: 'bg-accent-400 text-dark-950 hover:bg-accent-300 hover:shadow-lg hover:shadow-accent-400/20 hover:-translate-y-0.5 active:translate-y-0',
        destructive: 'bg-red-500 text-white hover:bg-red-400 hover:shadow-lg hover:shadow-red-500/20 hover:-translate-y-0.5 active:translate-y-0',
        outline: 'border-2 border-dark-700 bg-transparent text-dark-50 hover:bg-dark-800 hover:border-dark-600 hover:shadow-lg',
        secondary: 'bg-dark-800 text-dark-50 hover:bg-dark-700 hover:shadow-lg',
        ghost: 'hover:bg-dark-800 text-dark-200 hover:text-dark-50',
        link: 'text-accent-400 underline-offset-4 hover:underline hover:text-accent-300',
      },
      size: {
        default: 'h-12 px-6 py-3',
        sm: 'h-10 px-4 py-2 text-xs',
        lg: 'h-14 px-10 py-4 text-base',
        icon: 'h-12 w-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
